import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, UntypedFormArray, UntypedFormGroup} from "@angular/forms";
import {NgDialogAnimationService} from 'src/app/services/core/ng-dialog-animation.service';
import {ToasterService} from "../../../../services/core/toaster.service";
import {
  ObserverParticipantPopupComponent
} from "../../../sale/task/add-task/add-checklist/observer-participant-popup/observer-participant-popup.component";
import {TicketUserPopupComponent, ticketPeopleKey, ticketPeopleTypeLabel} from "../ticket-user-popup/ticket-user-popup.component";
import {
  TicketChecklistItemsPopupComponent,
  checklistItemKindIcon,
  pruneChecklistItems,
  ticketItemKey
} from "../ticket-checklist-items-popup/ticket-checklist-items-popup.component";

@Component({
    selector: 'app-ticket-checklist',
    templateUrl: './ticket-checklist.component.html',
    styleUrls: ['./ticket-checklist.component.scss'],
    standalone: false
})
export class TicketChecklistComponent implements OnInit {
  public isImportant: boolean = false;
  public CONST_OBSERVER_TYPE: string = 'observer';
  public CONST_PARTICIPANT_TYPE: string = 'participant';
  @Output() callback = new EventEmitter();
  @Input() checkLists: any;
  @Input() model: any = {};
  /** The ticket's Materials & Services line items — what a checklist item can link to. */
  @Input() ticketItems: any[] | null = null;
  /** Job tickets only, mirroring the Materials & Services tab. */
  @Input() showMaterials: boolean = false;

  readonly kindIcon = checklistItemKindIcon;
  readonly lineItemKey = ticketItemKey;

  public sectionForm: UntypedFormGroup = new UntypedFormGroup({
    checklists: new UntypedFormArray([]),
  })
  constructor(
    public dialog: NgDialogAnimationService,
    private fb: FormBuilder,
    public toasterService: ToasterService
  ) { }

  // Calculate completion percentage for progress bar
  get completionPercentage(): number {
    const total = this.checklists().length;
    if (total === 0) return 0;
    const completed = this.checklists().controls.filter(
      control => control.get('is_completed')?.value === true
    ).length;
    return Math.round((completed / total) * 100);
  }

  // Get count of completed items
  get completedCount(): number {
    return this.checklists().controls.filter(
      control => control.get('is_completed')?.value === true
    ).length;
  }

  // Get total count of items
  get totalCount(): number {
    return this.checklists().length;
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('changes', changes);
    if (changes.model) {
      this.model = changes.model.currentValue;
    }

    // Check if checkLists actually changed and has data
    if (changes.checkLists && changes.checkLists.currentValue) {
      const newCheckLists = changes.checkLists.currentValue;

      // Clear and repopulate when data arrives
      if (newCheckLists.length > 0) {
        this.checklists().clear();
        newCheckLists.forEach((item: any) => {
          this.addChecklist(item, true, false);
        });
      }
    }

    if (changes.ticketItems) {
      this.pruneRemovedLineItems();
    }
  }

  /**
   * A line item removed from Materials & Services is unlinked server-side; drop
   * it from any checklist row here too so the next save doesn't re-send it.
   */
  private pruneRemovedLineItems() {
    let changed = false;
    this.checklists().controls.forEach(row => {
      const kept = pruneChecklistItems(row.get('items')?.value, this.ticketItems as any[]);
      if (kept) {
        row.get('items')?.setValue(kept);
        changed = true;
      }
    });
    if (changed) {
      this.getDataForParent();
    }
  }

  openLineItemsPopup(index: any) {
    const dialogRef = this.dialog.open(TicketChecklistItemsPopupComponent, {
      width: '600px',
      maxWidth: '95vw',
      // Top-anchored: the popup grows as the linked strip fills, and a centred
      // dialog would jump upward under the pointer each time.
      position: { top: '12vh' },
      data: {
        items: this.ticketItems || [],
        selected: this.checklists().at(index)?.get('items')?.value || [],
        index: index,
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && Array.isArray(result.items)) {
        this.checklists().at(result.index)?.get('items')?.setValue(result.items);
        this.getDataForParent();
      }
    });
  }

  removeLineItem(index: any, item: any) {
    const control = this.checklists().at(index)?.get('items');
    const key = ticketItemKey(item);
    control?.setValue((control.value || []).filter((row: any) => ticketItemKey(row) !== key));
    this.getDataForParent();
  }

  /** Tooltip for the collapsed row's line-item count. */
  lineItemsSummary(index: any): string {
    return (this.checklists().at(index)?.get('items')?.value || [])
      .map((item: any) => `${item.item_name} (${item.quantity ?? '—'})`)
      .join(', ');
  }

  openObserverParticipantPopup(event: any,type: any,index: any) {
    const field = type == this.CONST_OBSERVER_TYPE ? 'observers' : 'participants';
    const dialogRef = this.dialog.open(TicketUserPopupComponent,{
      width: '420px',
      height: '500px',
      data: {
        attachAbleUser: ['Users', 'Vendors', 'Customers', 'Subcontractors'],
        multiple: true,
        selected: this.checklists().at(index)?.get(field)?.value || [],
        type: type,
        index: index,
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.items?.length) {
        this.addUsers(result.index, field, result.items);
      }
    })
  }

  /** Appends picked people to a checklist row, skipping any already on it (same type + id). */
  addUsers(index: any, field: string, items: any[]) {
    const control = this.checklists().at(index)?.get(field);
    const current = control?.getRawValue() || [];
    const existing = new Set(current.map(ticketPeopleKey));
    const additions = items.filter((item: any) => !existing.has(ticketPeopleKey(item)));
    if (additions.length) {
      control?.setValue([...current, ...additions]);
      this.getDataForParent();
    }
  }

  removeCheckList(index: any) {
    this.checklists().removeAt(index);
  }

  isImportantCheckBox(index: any) {
    const value = this.checklists().at(index).get('is_important')?.value;
    if(value == true) {
      this.checklists().at(index).get('is_important')?.setValue(false);
    } else {
      this.checklists().at(index).get('is_important')?.setValue(true);
    }
    // this.isImportant = !this.isImportant;
    this.getDataForParent();
  }

  checklists(): UntypedFormArray {
    return this.sectionForm.get('checklists') as UntypedFormArray;
  }

  addChecklist(checklist: any = '',viewMode: boolean = false,editMode:boolean = true) {
    this.checklists().push(this.newChecklist(checklist,viewMode,editMode));
    this.getDataForParent();
  }


  newChecklist(checklist: any = '',viewMode: boolean = false,editMode:boolean = true) {
    return this.fb.group({
      id: [checklist?.id || null],
      text: [checklist ? checklist?.text : ''],
      observers: [checklist ? checklist?.observers : []],
      participants: [checklist ? checklist?.participants : []],
      items: [checklist?.items || []],
      is_completed: [checklist ? checklist?.is_completed : false],
      is_important: [checklist ? checklist?.is_important : false],
      view_mode: [viewMode],
      edit_mode: [editMode],
    })
  }

  getDataForParent() {
    const checklists = this.sectionForm.get('checklists')?.value;
    console.log('getDataForParent', checklists);
    this.callback.emit(checklists);
  }

  removeUsers(index: any,dataArray: any) {
    dataArray.splice(index,1);
    this.getDataForParent();
  }

  /**
   * Ticket-level assignees, participants, then observers, flattened for the
   * collapsed-row avatar stack. Every checklist row shows the same
   * ticket-wide People list rather than a per-item assignment.
   */
  checklistPeople(index: any) {
    const assignees = (this.model?.responsible_persons || []).map((user: any) => ({
      user,
      role: 'Assignee',
    }));
    const participants = (this.model?.participants || []).map((user: any) => ({
      user,
      role: 'Participant',
    }));
    const observers = (this.model?.observers || []).map((user: any) => ({
      user,
      role: 'Observer',
    }));
    return [...assignees, ...participants, ...observers];
  }

  getInitials(name: string): string {
    return (name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  }

  /**
   * Colour by person, not by role.
   *
   * A role-based palette gave every participant on a row the same fill, so three
   * participants read as one indistinguishable blob. The role is still carried by
   * the tooltip, which is where it is actually legible.
   */
  private readonly AVATAR_COLORS: string[] = [
    '#ff6600', '#2563eb', '#14b8a6', '#8b5cf6', '#ec4899', '#f59e0b',
    '#10b981', '#6366f1', '#ef4444', '#0ea5e9', '#84cc16', '#d946ef',
  ];

  /**
   * Stable hash of the user id (name as fallback) so one person keeps the same
   * colour across rows, tabs and reloads rather than shifting with list order.
   */
  getAvatarColor(person: any): string {
    const key = String(person?.user?.id ?? person?.user?.full_name ?? '');
    if (!key) return '#6b7280';
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
    }
    return this.AVATAR_COLORS[Math.abs(hash) % this.AVATAR_COLORS.length];
  }

  /** Set on toolbar mousedown so the following blur does not collapse the row. */
  private suppressBlurCollapse = false;

  onToolbarMouseDown(event: MouseEvent) {
    // preventDefault stops the browser from shifting focus off the checklist
    // input at all, so blur never fires for a toolbar click in the first
    // place — more robust than racing a flag against it.
    event.preventDefault();
    // Kept as a defense-in-depth fallback in case preventDefault doesn't
    // suppress focus-shift in some environment (e.g. touch).
    this.suppressBlurCollapse = true;
  }

  onChecklistBlur(index: any) {
    if (this.suppressBlurCollapse) {
      this.suppressBlurCollapse = false;
      return;
    }
    // Delegate so this component's existing empty-row semantics are preserved.
    this.ediViewChecklist(index, true);
  }

  ediViewChecklist(index: any,viewMode:boolean = false,editMode:boolean = false) {
    const text = this.checklists().at(index).get('text')?.value;
    if(!text) {
      this.checklists().removeAt(index);
      return;
    }
    if(editMode) {
      this.checklists().at(index).get('view_mode')?.setValue(false);
      this.checklists().at(index).get('edit_mode')?.setValue(true);
    }
    if(viewMode) {
      this.checklists().at(index).get('view_mode')?.setValue(true);
      this.checklists().at(index).get('edit_mode')?.setValue(false);
    }

    this.getDataForParent();
  }
  onChangeCheckbox(e: any){
     this.getDataForParent();
  }
  getChipTypeLabel(user: any): string {
    return ticketPeopleTypeLabel(user);
  }

  getChipNameUsingType( user: any) {
    let name = '--';
    if (user?.full_name){
      name = user?.full_name;
    }else if (user?.name){
      name = user?.name;
    }
    else if (user?.company_name){
      name = user?.company_name;
    }
    else if (user?.full_title) {
      name = user?.full_title;
    }
    return name;
  }
}
